// third-party
import { createSlice } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { filterQueryString, returnFiltersQueryString } from 'helpers/objectFunctions';

// project imports
import { ARYABHATTA_API_DOMAIN_V1 } from 'config';
import { devConsolelog } from 'helpers/utilityFunctions';
import axios from 'utils/axios';
import { durationFilterData } from '../../views/pages/member-priority/constant';
import { DEFAULT_ERROR_MESSAGE } from '../constant';
import { dispatch } from '../index';
import { openSnackbar } from './snackbar';

const defaultStartDate = new Date(Date.now());
const defaultSearchStartDate = new Date(Date.now());
const defaultBookingStartDate = new Date(Date.now());
const defaultRequirementStartDate = new Date(Date.now());
const defaultStatsStartDate = new Date(Date.now());
const defaultEndDate = new Date(Date.now());
const currentDate = new Date(Date.now());

export const SEARCH_TYPE = 'search';
export const BOOKINGS_TYPE = 'booking';
export const REQUIREMENTS_TYPE = 'requirement';

const initialState = {
    error: null,
    data: {
        data1: {},
        data2: {},
        data3: {},
        data4: {},
        tableData: {},
        utilities: {}
    },
    filterOptions: {},
    isLoading: true,
    filters: {
        // status: [{ id: 'open', name: 'open' }]
        // query_type: [{ id: 2, name: 2 }]
    },
    additionalFiltersData: {},
    cardViewFilters: {
        type: {
            type: { id: 3, name: 'Flights' }
        },
        category: {
            category: { id: 3, name: 'Searches' }
        },
        last_created_at: {
            start_date: format(
                new Date(defaultStartDate.setDate(defaultStartDate.getDate() - durationFilterData.last_7_days)),
                'yyyy-MM-dd'
            ),
            end_date: format(defaultEndDate, 'yyyy-MM-dd'),
            isDateFilter: true
        }
    },
    tableViewFilters: {
        last_created_at: {
            start_date: format(new Date(currentDate.setMonth(currentDate.getMonth() - 5)), 'yyyy-MM-dd'),
            end_date: format(defaultEndDate, 'yyyy-MM-dd'),
            isDateFilter: true
        },
        type: {
            type: { id: 3 }
        },
        category: {
            category: { id: 3 }
        },
        time_range: {
            time_range: { id: 'month' }
        }
    },
    ticketData: {},
    memberSearchesFilter: {
        created_at: {
            start_date: format(new Date(defaultSearchStartDate.setDate(defaultSearchStartDate.getDate() - 6)), 'yyyy-MM-dd'),
            end_date: format(defaultEndDate, 'yyyy-MM-dd'),
            isDateFilter: true
        }
    },
    memberBookingsFilter: {
        created_at: {
            start_date: format(new Date(defaultBookingStartDate.setDate(defaultBookingStartDate.getDate() - 29)), 'yyyy-MM-dd'),
            end_date: format(defaultEndDate, 'yyyy-MM-dd'),
            isDateFilter: true
        }
    },
    memberRequirementsFilter: {
        created_at: {
            start_date: format(new Date(defaultRequirementStartDate.setDate(defaultRequirementStartDate.getDate() - 89)), 'yyyy-MM-dd'),
            end_date: format(defaultEndDate, 'yyyy-MM-dd'),
            isDateFilter: true,
            selectedType: 'last_90_days'
        }
    },
    summaryStatsFilter: {
        created_at: {
            start_date: format(new Date(defaultStatsStartDate.setDate(defaultStatsStartDate.getDate() - 13)), 'yyyy-MM-dd'),
            end_date: format(defaultEndDate, 'yyyy-MM-dd'),
            isDateFilter: true
        }
    },
    memberSearches: {},
    memberBookings: {},
    memberRequirements: {},
    memberFollowupData: {
        action: null,
        campaign: null,
        next_action_at: null,
        remarks: ''
    },
    bookingListData: {},
    editableRequirementData: {},
    statsSummary: {},
    bookingTarget: {},
    followUpData: {},
    businessDocuments: {},
    escalationsData: {},
    escalationFilters: {}
};

const slice = createSlice({
    name: 'memberPriority',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        // setData
        setData(state, action) {
            const { count = 0, next = null, previous = null, results = {}, page = 0, activityState } = action.payload;
            const { utilities = {}, member_priority_list: memberPriority = [] } = results || {};

            const currentPriorityTypeData = state.data[`data${activityState}`];

            currentPriorityTypeData.count = count;
            currentPriorityTypeData.next = next;
            currentPriorityTypeData.previous = previous;
            state.data.utilities = utilities;
            currentPriorityTypeData.memberPriority = memberPriority;
            currentPriorityTypeData.currentPage = page;
        },

        setFilters(state, action) {
            devConsolelog('data in state filters', action.payload, state.filters);
            state.filters = action.payload;
        },

        setBookingListData(state, action) {
            const { count = 0, next = null, previous = null, results = {}, page = 0 } = action.payload;
            const { utilities = {}, booking_list: bookingList = [] } = results || {};
            state.bookingListData.count = count;
            state.bookingListData.next = next;
            state.bookingListData.previous = previous;
            state.bookingListData.utilities = utilities;
            state.bookingListData.bookingList = bookingList;
            state.bookingListData.currentPage = page;
        },

        // append data from previous state in current state
        appendListData(state, action) {
            const { count = 0, next = null, results = {}, page = 0, activityState } = action.payload;
            const { utilities = {}, member_priority_list: memberPriority = [] } = results || {};

            const currentPriorityTypeData = state.data[`data${activityState}`];

            currentPriorityTypeData.count = count;
            currentPriorityTypeData.next = next;
            currentPriorityTypeData.utilities = utilities;
            currentPriorityTypeData.currentPage = page;

            currentPriorityTypeData.memberPriority = currentPriorityTypeData.memberPriority.concat([...memberPriority]);
        },

        setTableFilters(state, action) {
            state.tableViewFilters = action.payload;
        },

        setCardFilters(state, action) {
            state.cardViewFilters = action.payload;
        },

        setIsLoading(state, action) {
            const { loadingField = '', isLoading = false } = action.payload || {};
            state[loadingField] = isLoading;
        },

        setAdditionalFiltersData(state, action) {
            const newObj = { ...state.additionalFiltersData };
            state.additionalFiltersData = { ...newObj, ...action.payload };
        },

        setSelectedObjectData(state, action) {
            state.selectedMemberData = action.payload;
        },

        setSelectedMember(state, action) {
            state.selectedMember = action.payload;
        },

        setRequirementsData(state, action) {
            state.requirementsData = action.payload || {};
        },

        setStatsData(state, action) {
            state.statsSummary = action.payload || {};
        },

        setMemberSearch(state, action) {
            const { searches = {} } = action.payload || {};
            state.memberSearches.searches = searches;
        },

        setMemberSearchesFilter(state, action) {
            state.memberSearchesFilter = action.payload;
        },

        setMemberBookingsFilter(state, action) {
            state.memberBookingsFilter = action.payload;
        },

        setMemberRequirementsFilter(state, action) {
            state.memberRequirementsFilter = action.payload;
        },

        setMemberRequirement(state, action) {
            const { requirements = {} } = action.payload || {};
            state.memberRequirements.requirements = requirements;
        },

        setMemberBookings(state, action) {
            const { bookings = {}, member_details: memberDetails = {} } = action.payload || {};
            state.memberBookings.bookings = bookings;
            state.memberBookings.memberDetails = memberDetails;
        },

        setMembersTicketData(state, action) {
            devConsolelog('members ticket count', state, action.payload);
            const { count = 0, next = null, previous = null, results = {}, page = 0 } = action.payload || {};
            const { utilities = {}, open_close_system: membersTicketsList = [] } = results || {};
            state.ticketData.count = count;
            state.ticketData.next = next;
            state.ticketData.previous = previous;
            state.ticketData.utilities = utilities;
            state.ticketData.membersTicketsList = membersTicketsList;
            state.ticketData.currentPage = page;
        },

        setTableData(state, action) {
            const { count = 0, next = null, previous = null, results = {}, page = 0 } = action.payload || {};
            const { utilities = {}, member_priority_list: memberPriorityList = [] } = results || {};
            state.data.tableData.count = count;
            state.data.tableData.next = next;
            state.data.tableData.previous = previous;
            state.data.utilities = utilities;
            state.data.tableData.memberPriorityList = memberPriorityList;
            state.data.tableData.currentPage = page;
        },

        setMemberFollowupData(state, action) {
            const { action: actionType = null, campaign = null, next_action_at: nextActionAt = '', remarks = '' } = action.payload || {};
            state.memberFollowupData.action = actionType;
            state.memberFollowupData.campaign = campaign;
            state.memberFollowupData.next_action_at = nextActionAt;
            state.memberFollowupData.remarks = remarks;
        },

        setMemberRequirementEditableData(state, action) {
            state.editableRequirementData = action.payload || {};
        },

        setTransactionTypeData(state, action) {
            state.filterOptions.transaction_type = action.payload;
        },

        setBookingFilters(state, action) {
            state.bookingFilters = action.payload;
        },

        setBookingTarget(state, action) {
            state.bookingTarget = action.payload;
        },

        setMemberCode(state, action) {
            const { member_code: memberCode = '' } = action.payload;
            state.memberCode = memberCode;
        },

        setFollowUpData(state, action) {
            const { count = 0, next = null, previous = null, currentPage = 0, results = [] } = action.payload || {};
            state.followUpData.count = count;
            state.followUpData.next = next;
            state.followUpData.previous = previous;
            state.followUpData.results = results;
            state.followUpData.currentPage = currentPage;
        },

        setEscalationsData(state, action) {
            const { count = 0, next = null, previous = null, currentPage = 0, results = [] } = action.payload || {};
            state.escalationsData.count = count;
            state.escalationsData.next = next;
            state.escalationsData.previous = previous;
            state.escalationsData.results = results;
            state.escalationsData.currentPage = currentPage;
        },

        setDestinationExpertData(state, action) {
            state.filterOptions.destinationExpert = action.payload;
        },

        setBusinessDocuments(state, action) {
            state.businessDocuments = action.payload;
        },

        setEscalationFilters(state, action) {
            state.escalationFilters = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

export function getMemberPriorityData(activityState, pageChangeUrl = '', filterType = '', newPage = 0) {
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/member-priority/?`;
        if (pageChangeUrl) {
            url = `${pageChangeUrl}&`;
        }

        if (filterType) {
            const finalFilterQueryString = filterQueryString(filterType);

            url = `${url}${finalFilterQueryString}&activity_state=${activityState}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            if (!newPage) {
                dispatch(slice.actions.setData({ ...response.data, activityState, page: newPage || 0 }));
            } else {
                dispatch(slice.actions.appendListData({ ...response.data, activityState, page: newPage || 0 }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getMemberData(memCode = '') {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/${memCode}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setSelectedObjectData(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getMemberStatsData(memCode = '', filterType = '') {
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}members/${memCode}/stats`;
        if (filterType) {
            const finalFilterQueryString = filterQueryString(filterType);

            url = `${url}?${finalFilterQueryString}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setStatsData(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// export function getIndividualMembersTicketsData(pageChangeUrl = '', memberCode = '', newPage = 0) {
//     return async () => {
//         let url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/?status=open&member=${memberCode}&assigned_to=true`;
//         if (pageChangeUrl) {
//             url = pageChangeUrl;
//         }
//         try {
//             const response = await axios.get(url, {
//                 headers: {
//                     // Authorization: authHardcodedToken,
//                     source: 'mweb'
//                 }
//             });
//             dispatch(slice.actions.setMembersTicketData({ ...response.data, page: newPage || 0 }));
//         } catch (error) {
//             dispatch(slice.actions.hasError(error));
//         }
//     };
// }

export function getIndividualMembersTicketsData(pageChangeUrl = '', filterType = '', memberCode = '', newPage = 0) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isDataLoading', isLoading: true }));
    return async () => {
        devConsolelog('getIndividualMembersTicketsData url from params', pageChangeUrl, filterType, newPage, memberCode);
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/?search=${memberCode}`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
            // if (newPage !== 0) dispatch(slice.actions.setCurrentPageUrl(pageChangeUrl));
        }
        if (filterType) {
            const finalFilterQueryString = filterQueryString(filterType);
            devConsolelog('filter56url', url, finalFilterQueryString, filterType);
            url = `${url}&${finalFilterQueryString}`;
            // if (newPage === 0) dispatch(slice.actions.setCurrentPageUrl(url));
        }
        devConsolelog('rtref454', url);
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response getOpenCloseTicketsData', response);
            dispatch(slice.actions.setMembersTicketData({ ...response.data, page: newPage || 0 }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isDataLoading', isLoading: false }));
        } catch (error) {
            devConsolelog('error ', error);
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isDataLoading', isLoading: false }));
        }
    };
}

export const getAdditionalFilterData = async (filterName = '') => {
    const url = `${ARYABHATTA_API_DOMAIN_V1}travsys/${filterName}`;
    try {
        const response = await axios.get(url);
        devConsolelog('response tags data', response.data);
        const { results = [] } = response.data;
        const data = { [filterName]: results };
        dispatch(slice.actions.setAdditionalFiltersData(data));
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        const { error: errorObj = {}, detail = '' } = error || {};
        const { errorMessage = '' } = errorObj || {};
        dispatch(
            openSnackbar({
                open: true,
                message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                variant: 'alert',
                alert: {
                    color: 'error'
                },
                close: false
            })
        );
    }
};

export function getTableViewData(pageChangeUrl = '', filterType = '', newPage = 0) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isTableDataLoading', isLoading: true }));
    return async () => {
        // activity_state=1&type=1&start_date=2022-08-11&end_date=2023-01-11&table_view=table_view&time_range=month
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/member-priority/?table_view=table_view&page_size=10`;
        if (pageChangeUrl) {
            url = `${pageChangeUrl}&table_view=table_view&page_size=10`;
        }
        if (filterType) {
            const finalFilterQueryString = filterQueryString(filterType);

            url = `${url}&${finalFilterQueryString}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setTableData({ ...response.data, page: newPage || 0 }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isTableDataLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isTableDataLoading', isLoading: false }));
        }
    };
}

export const setLoadingState =
    (isLoading = {}) =>
    () => {
        dispatch(slice.actions.setIsLoading(isLoading));
    };

export const setMember =
    (data = {}) =>
    () => {
        dispatch(slice.actions.setSelectedMember(data));
    };

export function getMemberStats(id, filterType = '', page = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isCountLoading', isLoading: true }));
    return async () => {
        devConsolelog('id from params', id, filterType);
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/${id}?`;
        if (filterType) {
            const finalFilterQueryString = filterQueryString(filterType);
            devConsolelog('filter url', finalFilterQueryString);
            url = `${url}&${finalFilterQueryString}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getMemberDetailsData', response.data);
            if (page === SEARCH_TYPE) {
                await dispatch(slice.actions.setMemberSearch({ ...response.data }));
            } else if (page === BOOKINGS_TYPE) {
                await dispatch(slice.actions.setMemberBookings({ ...response.data }));
            } else if (page === REQUIREMENTS_TYPE) {
                await dispatch(slice.actions.setMemberRequirement({ ...response.data }));
            }
            dispatch(slice.actions.setIsLoading({ loadingField: 'isCountLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );

            dispatch(slice.actions.setIsLoading({ loadingField: 'isCountLoading', isLoading: false }));
        }
    };
}

export function getRequirementsData(pageChangeUrl = '', memberCode = '', newPage = 0) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isRequirementDataLoading', isLoading: true }));
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}lms/requirements/?member_code=${memberCode}&page_size=10&type=open`;
        if (pageChangeUrl) {
            url = `${pageChangeUrl}&member_code=${memberCode}&page_size=10&type=open`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setRequirementsData({ ...response.data, currentPage: newPage || 0 }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isRequirementDataLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isRequirementDataLoading', isLoading: false }));
        }
    };
}

export function patchCurrentRequirementData(memberCode, updatedRequirementData) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}lms/requirements/?member_code=${memberCode}&page_size=10&type=open`;
        try {
            const response = await axios.patch(url, updatedRequirementData, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response editTicketData', response.data);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getFollowUpData(memberCode = '', pageChangeUrl = '', newPage = 0) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isRecentFollowUpLoading', isLoading: true }));
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/${memberCode}/activities/`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setFollowUpData({ ...response.data, currentPage: newPage || 0 }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isRecentFollowUpLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );

            dispatch(slice.actions.setIsLoading({ loadingField: 'isRecentFollowUpLoading', isLoading: false }));
        }
    };
}

export function addFollowUpData(memberCode, followUpData) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/${memberCode}/activities/`;
        try {
            const response = await axios.post(url, followUpData, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response editTicketData', response.data);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getTransactionTypeData() {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}bms/bookings/transaction-types/`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response transaction type data', response.data);
            dispatch(slice.actions.setTransactionTypeData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {} } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getBookingListDataAndFilters(memberCode = '', pageChangeUrl = '', filterType = '', newPage = 0) {
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/bookings/?buyer=${memberCode}&ordering=-created_at&`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
        }
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);

            url = `${url}${filterQueryString}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });

            dispatch(slice.actions.setBookingListData({ ...response.data, page: filterType ? 0 : newPage }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getBookingTarget(memberCode = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingTargetLoading', isLoading: true }));
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}members/${memberCode}/booking-targets`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getBookingTarget', response.data);
            dispatch(slice.actions.setBookingTarget({ ...response.data }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingTargetLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingTargetLoading', isLoading: false }));
        }
    };
}

export function postBookingTarget(memberCode, bookingTarget) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}members/${memberCode}/booking-targets`;
        try {
            await axios.post(url, bookingTarget, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            await dispatch(
                openSnackbar({
                    open: true,
                    message: `Booking Target Added`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.hasError(''));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {} } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getMemberCodeFromId(memberId = '') {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}members/${memberId}/get_code`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getMemberCode', response.data);
            dispatch(slice.actions.setMemberCode({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getDestinationExpertData() {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}users/travclan-admin`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getDestinationExpertData', response.data);
            dispatch(slice.actions.setDestinationExpertData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {} } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function patchMemberDetails(memberCode, updatedMemberData) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/${memberCode}`;
        try {
            await axios.patch(url, updatedMemberData, {
                headers: {
                    source: 'mweb'
                }
            });

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Member Details Updated`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getBusinessDocuments(orgCode = '', pageChangeUrl = '', newPage = 0) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isBusinessDocsLoading', isLoading: true }));
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}organization/${orgCode}/business-documents/`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getBusinessDocuments', response.data);
            dispatch(slice.actions.setBusinessDocuments({ ...response.data, currentPage: newPage || 0 }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBusinessDocsLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBusinessDocsLoading', isLoading: false }));
        }
    };
}

export function getEscalations(pageChangeUrl = '', filterType = '', newPage = 0) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isEscalationsLoading', isLoading: true }));
    return async () => {
        devConsolelog('getEscalations url from params', pageChangeUrl, newPage);
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
        }
        if (filterType) {
            const finalFilterQueryString = filterQueryString(filterType);
            console.log('filter url', filterQueryString);
            url = `${url}?${finalFilterQueryString}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getOpenCloseTicketsData', response);
            dispatch(slice.actions.setEscalationsData({ ...response.data, currentPage: newPage || 0 }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isEscalationsLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isEscalationsLoading', isLoading: false }));
        }
    };
}

export const filterEscalationsData =
    (filter = '') =>
    async () => {
        dispatch(slice.actions.setEscalationFilters(filter || initialState.escalationFilters));
    };

export const getMemberSearchesFilter =
    (data = {}) =>
    () => {
        dispatch(slice.actions.setMemberSearchesFilter(data));
    };

export const getMemberBookingsFilter =
    (data = {}) =>
    () => {
        dispatch(slice.actions.setMemberBookingsFilter(data));
    };

export const getMemberRequirementsFilter =
    (data = {}) =>
    () => {
        dispatch(slice.actions.setMemberRequirementsFilter(data));
    };

export const filterPriorityData =
    (filter = '', viewType = '') =>
    async () => {
        if (viewType === 'card') {
            dispatch(slice.actions.setCardFilters(filter || initialState.cardViewFilters));
        } else if (viewType === 'table') {
            dispatch(slice.actions.setTableFilters(filter || initialState.tableViewFilters));
        }
    };

export const getMemberFollowupData =
    (data = {}) =>
    () => {
        dispatch(slice.actions.setMemberFollowupData(data));
    };

export const getMemberRequirementEditableData =
    (data = {}) =>
    () => {
        dispatch(slice.actions.setMemberRequirementEditableData(data));
    };

export const filterData =
    (filter = '') =>
    async () => {
        devConsolelog('in filters123', filter);
        dispatch(slice.actions.setFilters(filter || initialState.filters));
    };
